import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, Lock } from 'lucide-react';
import { base44 } from '@/api/base44Client';

export default function LeadForm({
  type = 'meta_audit',
  buttonLabel = 'Get Your Free Meta Ads Breakdown',
  showBusinessName = true,
  showAdSpend = true,
  showMessage = false,
  redirectTo = '/thank-you',
  compact = false,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: '', email: '', monthly_ad_spend: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid =
    /\S+@\S+\.\S+/.test(form.email) &&
    (!showBusinessName || form.business_name.trim().length > 1) &&
    (!showAdSpend || form.monthly_ad_spend.trim().length > 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await base44.functions.invoke('submitLead', { type, ...form });
      if (res.data?.error) throw new Error(res.data.error);
      navigate(redirectTo);
    } catch (err) {
      setError('Something went wrong sending your details. Please try again, or email sales@ahana.digital.');
      setBusy(false);
    }
  };

  const input =
    'w-full h-14 px-5 rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder:text-white/35 text-sm outline-none focus:border-[#3B82F6] transition-colors';

  return (
    <form onSubmit={onSubmit} className={compact ? 'space-y-3' : 'space-y-3.5'}>
      {showBusinessName && (
        <input className={input} placeholder="Business Name" value={form.business_name} onChange={set('business_name')} />
      )}
      <input className={input} type="email" placeholder="Email" value={form.email} onChange={set('email')} />
      {showAdSpend && (
        <select
          className={`${input} appearance-none ${form.monthly_ad_spend ? 'text-white' : 'text-white/35'}`}
          value={form.monthly_ad_spend}
          onChange={set('monthly_ad_spend')}
        >
          <option value="" className="bg-[#0B0B0B]">Current Monthly Ad Spend</option>
          {['Under $3,000', '$3,000 – $10,000', '$10,000 – $25,000', '$25,000 – $50,000', '$50,000 – $100,000', '$100,000+'].map(
            (o) => (
              <option key={o} value={o} className="bg-[#0B0B0B] text-white">
                {o}
              </option>
            )
          )}
        </select>
      )}
      {showMessage && (
        <textarea
          rows={4}
          className={`${input} h-auto py-4 resize-none`}
          placeholder="Tell us about the brand and the campaign you have in mind"
          value={form.message}
          onChange={set('message')}
        />
      )}

      <button
        type="submit"
        disabled={!valid || busy}
        className={`group w-full inline-flex items-center justify-center gap-2.5 h-16 rounded-lg text-base font-bold transition-all duration-300 ${
          valid && !busy ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8] blue-glow' : 'bg-white/10 text-white/40 cursor-not-allowed'
        }`}
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        {busy ? 'Sending…' : buttonLabel}
        {!busy && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
      </button>

      {error && <p className="text-sm text-red-400">{error}</p>}

      <p className="flex items-center gap-2 text-[11px] text-white/35">
        <Lock className="w-3 h-3" /> Your details are used only for this breakdown. No spam, no list-selling.
      </p>
    </form>
  );
}